<template>
  <b-container fluid>
    <b-row>
      <b-col sm="7"><h1>Purchase Orders</h1></b-col>
      <b-col sm="5" class="text-right">
        <b-button class="btn-sm" variant="primary" @click="reloadOrders"
          ><b-icon-arrow-repeat/> Reload list
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <h5 @click="showFilterRow = !showFilterRow" class="filtertoggle">
          Filter
          <b-icon-chevron-up scale="1.5" v-if="showFilterRow"/>
          <b-icon-chevron-down scale="1.5" v-else/>
        </h5>
      </b-col>
    </b-row>
    <b-row v-show="showFilterRow">
      <b-col md="3">
        <label for="textFilter">Text</label>
        <input
          type="text"
          v-model="textFilter"
          id="textFilter"
          class="form-control form-control-sm"
          placeholder="Search in OrderNo, Product-SKU"
        />
      </b-col>

      <b-col md="3">
        <b-form-group label="Creditor">
          <b-form-input v-model="creditorTextFilter" class="form-control-sm" placeholder="Creditor ID, Name, Email" />
        </b-form-group>
      </b-col>

      <b-col md="3" class="purchase-order-date-filter">
        <b-row class="purchase-order-date-filter-row">
          <b-col cols="6">
            <label for="purchase-order-from-date">From</label>
            <b-form-input
              type="text"
              class="form-control form-control-sm purchase-order-date"
              id="purchase-order-from-date"
              v-model="purchaseOrderFromDate"
              :class="!isValidDateRange ? 'error' : ''"
            />
          </b-col>
          <b-col cols="6">
            <label for="purchase-order-to-date">To</label>
            <input
              type="text"
              class="form-control form-control-sm purchase-order-date"
              id="purchase-order-to-date"
              v-model="purchaseOrderToDate"
              :class="!isValidDateRange ? 'error' : ''"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col id="btn_apply_purchase_order_date_filter_col">
            <b-button block variant="primary" size="sm" @click="reloadOrders()" :disabled="!isValidDateRange">
              Apply Date Filter
            </b-button>
            <b-popover
              target="btn_apply_purchase_order_date_filter_col"
              title="Invalid Date Filter"
              placement="bottom"
              :disabled="isValidDateRange"
              :show="!isValidDateRange"
              content="From-date must be before To-date and the timespan must be within one year"
            />
          </b-col>
        </b-row>
      </b-col>

      <b-col md="3">
        <b-form-group label="Type">
          <b-form-select v-model="typeSelected" :options="filterOptions.demand.type" size="sm" />
        </b-form-group>
      </b-col>
    </b-row>

    <hr />

    <b-row>
      <b-col class="align-items-center">
        <b-button variant="secondary" size="sm" block
          @click="resetFilter()"
          :disabled="!filterActive"
        >
          Reset Filter <b-icon-arrow-counterclockwise/>
        </b-button>
      </b-col>
    </b-row>

    <hr />

    <b-row>
      <b-col md="4">
        <b-row>
          <b-col cols="4">Page Size</b-col>
          <b-col cols="6">
            <ul class="pagination page-size-selection">
              <li
                v-for="pageSizeSelection in [10, 20, 50]"
                class="page-item"
                :class="{ active: pageSizeSelection === pageSize }"
                @click="pageSize = pageSizeSelection"
                :key="pageSizeSelection"
              >
                <div class="page-link">{{ pageSizeSelection }}</div>
              </li>
            </ul>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" md="8" class="text-right">
        Showing {{ firstResultInView }} - {{ lastResultInView }} of <strong>{{ numberOfFilteredOrders }}</strong> /
        Total
        {{ numberOfTotalOrders }}
      </b-col>
    </b-row>
    <hr />
    <b-table
      striped
      hover
      show-empty
      id="purchaseOrderTable"
      :fields="fields"
      :items="filteredOrders"
      :per-page="pageSize"
      :current-page="currentPage"
      :busy="isLoading || isApiError"
      @row-clicked="showDetails"
    >
      <div v-if="isApiError" slot="table-busy" class="text-center my-2">
        <strong style="color:red">Failed to retrieve API data</strong>
      </div>
      <template v-slot:table-busy> <b-spinner class="align-middle" />&nbsp; <strong>Loading...</strong> </template>

      <template v-slot:cell(details)="data">
        <strong>Demand</strong><br />
        Bought for :
        {{ data.item.demand !== undefined ? data.item.demand.buyForCompany : '--' }}
        <br />
        <strong>PurchaseOffer</strong><br />
        Creditor: <br />{{
          purchaseOffers[data.item.purchaseOfferId] !== undefined
            ? purchaseOffers[data.item.purchaseOfferId].userEmail
            : '--'
        }}<br />
        Comment :<br />{{
          purchaseOffers[data.item.purchaseOfferId] !== undefined
            ? purchaseOffers[data.item.purchaseOfferId].positionComment
            : '--'
        }}<br />
      </template>
      <template v-slot:cell(purchaseOrderNo)="data">
        {{ data.item.purchaseOrderNo || '--' }}
      </template>

      <template v-slot:cell(company)="data">
        {{ data.item.company || '--' }}
      </template>

      <template v-slot:cell(companyNumber)="data">
        {{ data.item.companyNo || '--' }}
      </template>

      <template v-slot:cell(purchaseOrderNoYoda)="data">
        {{ purchaseOrdersEfg[data.item.demandId] || '--' }}
      </template>

      <template v-slot:cell(demandOrderNo)="data">
        {{ data.item.orderNo || '--' }}
      </template>

      <template v-slot:cell(creditorId)="data">
        {{ userByCreditorId(data.value) !== null ? userByCreditorId(data.value).email : '#' + data.value }}
      </template>

      <template v-slot:cell(createdAt)="data">
        {{ formattedDate(data.value) }}
      </template>

      <template v-slot:cell(deliveryDate)="data">
        {{ formattedDate(data.value) }}
      </template>

      <template v-slot:cell(type)="data">
        <template v-if="data.item.demand">
          <b-badge variant="success" v-if="data.item.demand.type === DEMAND.TYPE.DIVA">DiVA </b-badge>
          <b-badge variant="warning" v-else-if="data.item.demand.type === DEMAND.TYPE.STRATEGIC">SD </b-badge>
          <b-badge variant="info" v-else-if="data.item.demand.type === DEMAND.TYPE.NEVER_OUT_OF_STOCK">NOS </b-badge>
        </template>
        <b-badge variant="danger" v-if="isDemandApiFailed" title="Demand data could not be retrieved">ERROR </b-badge>

        <b-spinner
          small
          v-if="!data.item.demand && purchaseOrdersDemandsApiStatus === ApiLoadingStatus.STATUS_LOADING"
          class="align-middle"
        />
      </template>

      <template v-slot:cell(error)="data">
        <b-badge variant="danger" v-if="data.item.divaSentError !== null">
          {{ data.item.divaSentError }}
        </b-badge>
        <b-badge variant="danger" v-if="data.item.yodaSentError !== null">
          {{ data.item.yodaSentError }}
        </b-badge>
      </template>
    </b-table>

    <b-pagination
      v-model="currentPage"
      :total-rows="numberOfFilteredOrders"
      :per-page="pageSize"
      aria-controls="purchaseOrderTable"
      align="center"
    />
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { GET_USER_ACTION, LOAD } from '@/store/actionTypes';
import { timeFormatMixin } from 'Mixins/timeFormatMixin';
import DemandConstants from 'src/constants/demand';
import Demand from 'Constants/demand';
import demandMixin from 'Mixins/demand';
import Pikaday from 'pikaday';
import { addWeeks, format, differenceInYears, parse } from 'date-fns';
import ApiLoadingStatus from '@/api/statusTypes';
import { GET_ORDERS, GET_ORDERS_EFG } from '@/store/getterTypes';

export default {
  name: 'AdminPurchaseOrderOverview',
  mixins: [timeFormatMixin, demandMixin],
  components: {},
  props: [],
  data() {
    return {
      fields: [
        {
          key: 'purchaseOrderNo',
          sortable: true,
        },
        {
          key: 'company',
        },
        {
          key: 'companyNumber',
        },
        {
          key: 'demandOrderNo',
          label: 'Customer Order No',
          sortable: true,
        },
        {
          key: 'productKey',
          sortable: true,
        },
        {
          key: 'variantKey',
          sortable: true,
        },
        {
          key: 'creditorId',
          label: 'Creditor',
        },
        { key: 'details' },
        {
          key: 'createdAt',
          sortable: true,
        },
        {
          key: 'deliveryDate',
          label: 'Expected Delivery',
          sortable: true,
        },
        {
          key: 'type',
        },
      ],
      typeMap: [],
      currentPage: 1,
      pageSize: 20,
      DEMAND: DemandConstants,

      showFilterRow: true,
      textFilter: '',
      creditorTextFilter: '',
      purchaseOrderFromDate: null,
      purchaseOrderToDate: null,
      typeSelected: '',
      ApiLoadingStatus,
      filterOptions: {
        demand: {
          type: [
            { value: '', text: 'All' },
            { value: Demand.TYPE.DIVA, text: 'Customer Order' },
            { value: Demand.TYPE.STRATEGIC, text: 'Strategic Stock' },
            {
              value: Demand.TYPE.NEVER_OUT_OF_STOCK,
              text: 'Never Out Of Stock',
            },
          ],
        },
      },
    };
  },
  created() {
    this.loadUsers();
    this.purchaseOrderFromDate = format(addWeeks(new Date(), -1), 'yyyy-MM-dd');
    this.purchaseOrderToDate = format(new Date(), 'yyyy-MM-dd');
    this.loadPurchaseOrders({
      fromDate: parse(this.purchaseOrderFromDate, 'yyyy-MM-dd', new Date()),
      toDate: parse(this.purchaseOrderToDate, 'yyyy-MM-dd', new Date()),
    });
  },
  computed: {
    ...mapGetters(['allUsers', 'byCreditorId', 'allDemands']),
    ...mapGetters({ userByCreditorId: 'byCreditorId' }),
    ...mapGetters('purchaseOrder', {
      purchaseOrders: GET_ORDERS,
      purchaseOrdersEfg: GET_ORDERS_EFG,
      purchaseOffers: 'getPurchaseOffers',
    }),
    ...mapState('purchaseOrder', {
      purchaseOrderApiStatus: (state) => state.status,
      purchaseOrdersDemandsApiStatus: (state) => state.purchaseOrdersDemandsStatus,
    }),
    isDemandApiFailed() {
      return this.purchaseOrdersDemandsApiStatus === ApiLoadingStatus.STATUS_ERROR_LOADING;
    },
    isLoading() {
      return this.purchaseOrderApiStatus === ApiLoadingStatus.STATUS_LOADING || this.purchaseOrderApiStatus === null;
    },
    filteredOrders() {
      return this.purchaseOrders.filter((order) => {
        let textFilter = this.textFilter.toLowerCase();
        if (
          this.textFilter !== '' &&
          (order.orderNo || '').toLowerCase().indexOf(textFilter) === -1 &&
          (order.purchaseOrderNo || '').toLowerCase().indexOf(textFilter) === -1 &&
          order.productKey.toLowerCase().indexOf(textFilter) === -1 &&
          order.variantKey.toLowerCase().indexOf(textFilter) === -1
        ) {
          return false;
        }

        if (this.typeSelected !== '') {
          if (!order.demand) return false;
          switch (true) {
            case this.typeSelected === Demand.TYPE.DIVA && !this.isDivaDemand(order.demand):
              return false;
            case this.typeSelected === Demand.TYPE.STRATEGIC && !this.isStrategicDemand(order.demand):
              return false;
            case this.typeSelected === Demand.TYPE.NEVER_OUT_OF_STOCK && !this.isNeverOutOfStockDemand(order.demand):
              return false;
          }
        }

        let creditorTextFilter = this.creditorTextFilter.toLowerCase();
        return !(
          (this.creditorTextFilter !== '' &&
            this.userByCreditorId(order.creditorId) === null &&
            order.creditorId.toString().indexOf(creditorTextFilter) === -1) ||
          (this.userByCreditorId(order.creditorId) !== null &&
            this.userByCreditorId(order.creditorId)
              .email.toLowerCase()
              .indexOf(creditorTextFilter) === -1)
        );
      });
    },
    isValidDateRange() {
      let fromDate = this.parseDate(this.purchaseOrderFromDate, 'yyyy-MM-dd', new Date());
      let toDate = parse(this.purchaseOrderToDate, 'yyyy-MM-dd', new Date());
      let isWithinOneYear = differenceInYears(toDate, fromDate) < 1;
      return fromDate <= toDate && isWithinOneYear;
    },
    filterActive() {
      return this.textFilter !== '' || this.creditorTextFilter !== '' || this.typeSelected !== '';
    },
    firstResultInView() {
      return Math.min(this.filteredOrders.length, (this.currentPage - 1) * this.pageSize + 1);
    },
    lastResultInView() {
      if (this.currentPage * this.pageSize > this.filteredOrders.length) {
        return this.filteredOrders.length;
      }
      return this.currentPage * this.pageSize;
    },
    numberOfFilteredOrders() {
      return this.filteredOrders.length;
    },
    numberOfTotalOrders() {
      return this.purchaseOrders.length;
    },
    isApiError() {
      return this.purchaseOrderApiStatus === ApiLoadingStatus.STATUS_ERROR_LOADING;
    },
  },
  methods: {
    ...mapActions('purchaseOrder', { loadPurchaseOrders: LOAD }),
    ...mapActions([GET_USER_ACTION]),
    loadUsers: function() {
      return this[GET_USER_ACTION]();
    },
    reloadOrders() {
      this.loadPurchaseOrders({
        fromDate: this.apiFormattedDateTime(this.purchaseOrderFromDate),
        toDate: this.apiFormattedDateTime(this.purchaseOrderToDate),
      });
    },
    resetFilter() {
      this.textFilter = '';
      this.creditorTextFilter = '';
      this.typeSelected = '';
    },
    showDetails(row) {
      row._showDetails = !row._showDetails;
    },
  },
  mounted() {
    let self = this;

    new Pikaday({
      field: document.getElementById('purchase-order-from-date'),
      format: 'yyyy-MM-dd',
      toString(date) {
        return self.formattedDate(date);
      },
      parse(dateString) {
        return self.parseDate(dateString);
      },
      onSelect() {
        self.purchaseOrderFromDate = this.toString();
      },
    });

    new Pikaday({
      field: document.getElementById('purchase-order-to-date'),
      format: 'yyyy-MM-dd',
      toString(date) {
        return self.formattedDate(date);
      },
      parse(dateString) {
        return self.parseDate(dateString);
      },
      onSelect() {
        self.purchaseOrderToDate = this.toString();
      },
    });
  },
};
</script>

<style lang="scss" scoped>
.filtertoggle {
  cursor: pointer;
}

.btn-reload-demands-and-purchase-orders {
  position: fixed;
  z-index: 100;
  right: 40px;
  top: 50px;
}

.purchase-order-date-filter {
  padding-bottom: 10px;
}

.purchase-order-date-filter-row {
  margin-bottom: 1em;
}

input.error {
  border-color: var(--red);
}
</style>
